import * as React from 'react';
import {useState} from 'react';
import {Form} from "@intelingu/core/components/form/Form";
import AsyncCreatableSelect from 'react-select/async-creatable';
import { components } from 'react-select';
import {Field} from "@intelingu/core/components/form/Field";
import {Checkbox} from "../../../partials/form/elements/Checkbox";
import {
    SearchTranslationsDocument,
    SearchTranslationsQuery, SearchTranslationsQueryVariables,
    UpdateWordTranslationInput, useCreateWordMutation,
    useGetTranslationForVocabularyQuery,
    useGetWordByIdQuery, useUpdateCardMutation,
    useUpdateWordTranslationMutation
} from "../../../../types.d";
import {getParam} from "../../../../utils/getParam";
import {FormGroup} from "../../../partials/form/elements/FormGroup";
import {SubmitButton} from "../../../partials/form/elements/SubmitButton";
import {translationObjectToInput} from "../../../lib/mappings/mappings";
import {IsNotEmpty} from "class-validator";
import {AudioField} from "../../../partials/form/elements/AudioField";
import {Redirect} from "react-router";
import debounce from "debounce-promise";
import {graphql} from "../../../../configs/graphql.client";

class Validator implements UpdateWordTranslationInput {

    @IsNotEmpty()
    translation;

    @IsNotEmpty()
    audio;

    separateMeanings;
}

const Menu = props => {
    if ((props.options.length < 2 || props.isLoading) && !props.options[0]?.disabled) {
        return <components.Menu {...props}>{ props.children }</components.Menu>
    }

    return (
        <>
            <components.Menu {...props}>
                <div style={{ padding: 14 }}>
                    Identical translation already exists! Please use more specific translation, by using additional synonyms. Use Google Translate to find most relevant synonyms.
                </div>
            </components.Menu>
        </>
    );
};

export const CreateTranslation = props => {
    const [saved, setSaved] = useState(false);
    const [createWordLoading, setCreateWordLoading] = useState(false);
    const cardId = getParam<number>('card_id');
    const editMode = !!cardId;

    const translation = useGetTranslationForVocabularyQuery({
        fetchPolicy: 'network-only',
        variables: { id: getParam<number>('translation_id') }
    });

    const [ createWord, { data: createdWord } ] = useCreateWordMutation({
        fetchPolicy: 'no-cache',
    });

    const defaultValue = useGetWordByIdQuery({
        fetchPolicy: 'network-only',
        skip: !translation.data?.wordTranslation?.translationWord,
        variables: { id: translation.data?.wordTranslation?.translationWord?.id }
    });

    const [ updateTranslation, { data: updatedTranslation } ] = useUpdateWordTranslationMutation({
        fetchPolicy: 'no-cache',
    });

    let updateCard;
    let updatedCard;

    if (cardId) {
        const [ updateCardMutation, { data: updatedCardData } ] = useUpdateCardMutation({
            fetchPolicy: 'no-cache'
        });

        updateCard = updateCardMutation;
        updatedCard = updatedCardData;
    }

    let translationValue = defaultValue.data ? {
        label: defaultValue.data.word.lemma,
        value: defaultValue.data.word.id,
    } : undefined;

    if (createdWord) {
        translationValue = {
            label: createdWord.createWord.lemma,
            value: createdWord.createWord.id,
        };
    }

    if (!translation.data) {
        return null;
    }

    const wordTranslation = translation.data.wordTranslation;

    if (saved) {
        if (editMode) {
            return <h2>Saved...</h2>
        }

        return <Redirect to={`/lp/vocabulary/?translation_id=${wordTranslation.id}`} />
    }

    return <div className={'index-wrapper'}>
        <div className={'index-page'}>
            <Form
                defaultData={translationObjectToInput(translation.data.wordTranslation)}
                validator={Validator}
                onSubmit={async (data, done) => {
                    await updateTranslation({
                        variables: {
                            id: wordTranslation.id,
                            input: data
                        }
                    });

                    if (updateCard) {
                        await updateCard({
                            variables: {
                                id: cardId,
                                input: {},
                                editMode: true
                            }
                        });
                    }

                    done();
                    setSaved(true);
                }}
            >
                <div className="ig-form-heading">
                    <h1 className="form-primary-title">Vocabulary Card: Design Great Content</h1>
                </div>

                <div className="row px-3">
                    <div className="col-4">
                        <label><strong>Source Language</strong>:</label>
                        <span className="mx-1">{ wordTranslation.sourceLanguage.name }</span>
                    </div>
                    <div className="col-8">
                        <label>
                            <strong>Translation Language</strong>:</label>
                        <span className="mx-1">{ wordTranslation.translationLanguage.name }</span>
                    </div>
                </div>

                <div className="row px-3">
                    <div className="col-4">
                        <label><strong>Part Of Speech</strong>:</label>
                        <span className="mx-1">{ wordTranslation.sourceWord.posDefinition } ({ wordTranslation.sourceWord.pos })</span>
                    </div>
                    <div className="col-8">
                        <label><strong>Position of Vocabulary</strong>:</label>
                        <span className="mx-1">{ translation.data.wordTranslation.sourceWord.rank }</span>
                    </div>
                </div>

                <FormGroup className={'mt-5'} label={'Vocabulary:'}>
                    <input
                        type={'text'}
                        className={'form-control'}
                        disabled={true}
                        style={{ color: 'black' }}
                        value={wordTranslation.sourceWord.lemma} />
                </FormGroup>

                { (defaultValue.data || (translation.data && !translation.data?.wordTranslation?.translationWord)) && (
                    <FormGroup className={'mt-2'} label={'Translation:'} required={true}>
                        <Field name={'translation'} render={props => (
                            <AsyncCreatableSelect
                                isDisabled={createWordLoading || translation.data.wordTranslation.sourceWord.predefinedTranslation}
                                cacheOptions
                                defaultOptions
                                loadOptions={debounce(async (query) => {
                                    const result = await graphql.client.query<SearchTranslationsQuery, SearchTranslationsQueryVariables>({
                                        query: SearchTranslationsDocument,
                                        fetchPolicy: 'network-only',
                                        variables: {
                                            query,
                                            sourceLanguage: wordTranslation.sourceLanguage.id,
                                            translationLanguage: wordTranslation.translationLanguage.id,
                                        }
                                    });

                                    return result.data.searchTranslations.map(result => ({
                                        label: result.text,
                                        value: result.id,
                                        disabled: true
                                    }));
                                }, 300)}
                                value={translationValue}
                                isOptionDisabled={(option) => {
                                    return option.disabled;
                                }}
                                components={{ Menu }}
                                isMulti={false}
                                onCreateOption={async value => {
                                    setCreateWordLoading(true);

                                    const result = await createWord({
                                        variables: {
                                            input: {
                                                language: wordTranslation.translationLanguage.id,
                                                lemma: value,
                                                postTitle: value,
                                            }
                                        }
                                    });

                                    const { id, lemma } = result.data.createWord;

                                    props.onChange(id);

                                    setCreateWordLoading(false);
                                }}
                                onChange={value => {}}
                            />
                        )} />
                    </FormGroup>
                )}

                <FormGroup className={'mt-2'}>
                    <Field
                        name={'separateMeanings'}
                        render={props => <Checkbox {...props} label={'Vocabulary has several separate meanings'} />} />
                </FormGroup>

                <FormGroup className={'mt-2'} label={'Translation audio* (record hotkey: Ctrl+1):'} required={true}>
                    <Field
                        name={'audio'}
                        render={props => <AudioField {...props} />} />
                </FormGroup>

                <div className={'col-12'}>
                    <Field submit render={props => <SubmitButton {...props}>Done</SubmitButton>} />
                </div>
            </Form>
        </div>
    </div>;
};
